import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiPlusCircle, FiFileText, FiFolder } from 'react-icons/fi';

const CommandPalette = ({ isOpen, onClose, onAddNote }) => {
  const [query, setQuery] = useState('');

  // Handle Cmd+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        isOpen ? onClose() : onClose(true); // Toggle logic handled at parent, just signal intent
      }
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleAdd = () => {
    onAddNote({
      id: Date.now().toString(),
      title: query || 'New Idea',
      content: 'Start typing your thoughts here...',
      x: window.innerWidth / 2 - 150, // rough center
      y: window.innerHeight / 2 - 100,
      tags: ['Uncategorized'],
      type: 'idea',
      folderId: 'f1'
    });
    setQuery('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-ink/30 backdrop-blur-sm z-50"
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            className="fixed top-[20vh] left-1/2 -translate-x-1/2 w-full max-w-xl bg-paper-light rounded-2xl shadow-2xl overflow-hidden z-50 border border-paper-dark"
          >
            <div className="flex items-center px-4 py-3 border-b border-paper-dark">
              <SafeIcon icon={FiSearch} className="text-ink-muted w-5 h-5 mr-3" />
              <input 
                autoFocus
                type="text" 
                placeholder="Type a command or search..." 
                className="flex-1 bg-transparent border-none outline-none text-ink text-lg placeholder:text-ink-muted/50"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              />
              <div className="text-xs text-ink-muted bg-paper-dark px-2 py-1 rounded">ESC to close</div>
            </div>

            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-ink-muted uppercase tracking-wider">
                Quick Actions
              </div>
              <button 
                onClick={handleAdd}
                className="w-full flex items-center px-3 py-3 rounded-lg hover:bg-maple-soft text-left group transition-colors"
              >
                <div className="bg-maple/10 p-2 rounded-md mr-3 group-hover:bg-maple/20 transition-colors">
                  <SafeIcon icon={FiPlusCircle} className="text-maple w-5 h-5" />
                </div>
                <div>
                  <div className="text-ink font-medium">Create new note</div>
                  <div className="text-xs text-ink-muted">Place a new thought on the canvas</div>
                </div>
              </button>
              
              <button className="w-full flex items-center px-3 py-3 rounded-lg hover:bg-paper-dark/50 text-left group transition-colors">
                <div className="bg-paper-dark p-2 rounded-md mr-3">
                  <SafeIcon icon={FiFolder} className="text-ink-muted w-5 h-5" />
                </div>
                <div>
                  <div className="text-ink font-medium">New Project Folder</div>
                  <div className="text-xs text-ink-muted">Organize your workspaces</div>
                </div>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;